/*
 * Skeleton loading placeholders — pulse-animated shapes
 * that replace content while data is loading.
 *
 * Much better perceived performance than showing "Loading…" text.
 *
 * ! file components/Skeleton.js
 * ---------------
 * \author: Tolga O. Atalay
 * \Affiliation: A2 Labs LLC / A12XT Inc.
 * \date: 2025
 */

import React from "react";

/** A single rectangular shimmer block */
export function SkeletonBlock({ width = "100%", height = 16, radius = 6, style }) {
  return (
    <div
      className="skeleton-block"
      style={{ width, height, borderRadius: radius, ...style }}
    />
  );
}

/** A skeleton card that mimics a stat card */
export function SkeletonCard({ style }) {
  return (
    <div className="skeleton-card" style={style}>
      <SkeletonBlock width={40} height={40} radius={10} />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
        <SkeletonBlock width="50%" height={10} />
        <SkeletonBlock width="70%" height={16} />
      </div>
    </div>
  );
}

/** A row of skeleton stat cards */
export function SkeletonStatRow({ count = 4 }) {
  return (
    <div className="skeleton-stat-row">
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonCard key={i} />
      ))}
    </div>
  );
}

/** A skeleton tile grid (for fleet overview) */
export function SkeletonTileGrid({ count = 6 }) {
  return (
    <div className="skeleton-tile-grid">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="skeleton-tile">
          <SkeletonBlock width={36} height={36} radius={10} />
          <SkeletonBlock width="60%" height={12} style={{ marginTop: 10 }} />
          <SkeletonBlock width="40%" height={10} style={{ marginTop: 6 }} />
        </div>
      ))}
    </div>
  );
}

/** A skeleton table (for pool / diagnostics) */
export function SkeletonTable({ rows = 5, cols = 4 }) {
  return (
    <div className="skeleton-table">
      <div className="skeleton-table-header">
        {Array.from({ length: cols }).map((_, i) => (
          <SkeletonBlock key={i} width="80%" height={10} />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="skeleton-table-row">
          {Array.from({ length: cols }).map((_, j) => (
            <SkeletonBlock key={j} width={`${50 + Math.random() * 40}%`} height={12} />
          ))}
        </div>
      ))}
    </div>
  );
}
